import type { FulfillmentOrder, FulfillmentOrderLine } from '../../domain/fulfillment';

/**
 * One line of a handed-over pick list: what left Tidal, when and for where
 * (tidaltech/koi#8206). Only completed orders count, so the numbers are as
 * complete as pick list usage.
 */
export interface ShippedLine {
  key: string;
  orderId: string;
  orderTitle: string;
  shippedAt: string;
  customerId: string;
  siteId: string;
  shipmentId?: string | undefined;
  item: string;
  quantity: number;
  unit: string;
  serials: string[];
}

export interface ShippedItemNames {
  /** Name for a serialized line, e.g. the part type ("Surface camera"). */
  serialized: (line: FulfillmentOrderLine) => string;
  /** Name for a stock line, e.g. the stock item's display name. */
  stock: (stockItemId: string) => string;
  unit: (stockItemId: string | undefined) => string;
}

export interface ShippedItemFilters {
  /** Inclusive dates as YYYY-MM-DD, compared on the handover date. */
  from?: string | undefined;
  to?: string | undefined;
  customerId?: string | undefined;
  siteId?: string | undefined;
  /** Matches the item name or any serial number. */
  query?: string | undefined;
}

export interface ShippedItemSummary {
  item: string;
  quantity: number;
  unit: string;
  orders: number;
  sites: number;
}

function orderTitle(order: FulfillmentOrder): string {
  return order.externalReference || `Order ${order.id.slice(0, 8)}`;
}

/** The packed lines of every completed order. Empty lines are left out. */
export function shippedLines(orders: FulfillmentOrder[], names: ShippedItemNames): ShippedLine[] {
  const out: ShippedLine[] = [];
  for (const order of orders) {
    if (order.state !== 'COMPLETED') continue;
    const shippedAt = order.handedOverAt ?? order.lastModified ?? '';
    for (const line of order.lines) {
      const serialized = line.requirement.type === 'SERIALIZED';
      const quantity = serialized ? line.itemIds.length : line.pickedQuantity;
      if (quantity <= 0) continue;
      const stockId = line.requirement.stockItemId;
      out.push({
        key: `${order.id}/${line.id}`,
        orderId: order.id,
        orderTitle: orderTitle(order),
        shippedAt,
        customerId: order.customerId,
        siteId: order.siteId,
        shipmentId: order.shipmentId,
        item: serialized ? names.serialized(line) : names.stock(stockId ?? ''),
        quantity,
        unit: serialized ? '' : names.unit(stockId),
        serials: serialized ? [...line.itemIds] : [],
      });
    }
  }
  return out.sort((a, b) => b.shippedAt.localeCompare(a.shippedAt));
}

export function filterShippedLines(
  lines: ShippedLine[],
  filters: ShippedItemFilters,
): ShippedLine[] {
  const needle = filters.query?.trim().toLowerCase() ?? '';
  return lines.filter((line) => {
    const day = line.shippedAt.slice(0, 10);
    if (filters.from && day < filters.from) return false;
    if (filters.to && day > filters.to) return false;
    if (filters.customerId && line.customerId !== filters.customerId) return false;
    if (filters.siteId && line.siteId !== filters.siteId) return false;
    if (
      needle &&
      !line.item.toLowerCase().includes(needle) &&
      !line.serials.some((serial) => serial.toLowerCase().includes(needle))
    ) {
      return false;
    }
    return true;
  });
}

/** Totals per item, largest first. Units differ per stock item, so they stay apart. */
export function summarizeShippedItems(lines: ShippedLine[]): ShippedItemSummary[] {
  const byItem = new Map<
    string,
    { item: string; unit: string; quantity: number; orders: Set<string>; sites: Set<string> }
  >();
  for (const line of lines) {
    const key = `${line.item}\u0000${line.unit}`;
    const entry = byItem.get(key) ?? {
      item: line.item,
      unit: line.unit,
      quantity: 0,
      orders: new Set<string>(),
      sites: new Set<string>(),
    };
    entry.quantity += line.quantity;
    entry.orders.add(line.orderId);
    entry.sites.add(line.siteId);
    byItem.set(key, entry);
  }
  return [...byItem.values()]
    .map((entry) => ({
      item: entry.item,
      unit: entry.unit,
      quantity: entry.quantity,
      orders: entry.orders.size,
      sites: entry.sites.size,
    }))
    .sort((a, b) => b.quantity - a.quantity || a.item.localeCompare(b.item));
}

function csvCell(value: string | number): string {
  return `"${String(value).replaceAll('"', '""')}"`;
}

/** The shipped lines as CSV, one row per line, for spreadsheets. */
export function shippedLinesCsv(
  lines: ShippedLine[],
  places: { customer: (id: string) => string; site: (id: string) => string },
): string {
  const header = [
    'Shipped',
    'Item',
    'Quantity',
    'Unit',
    'Serial numbers',
    'Customer',
    'Site',
    'Order',
  ];
  const rows = lines.map((line) =>
    [
      line.shippedAt.slice(0, 10),
      line.item,
      line.quantity,
      line.unit,
      line.serials.join(' '),
      places.customer(line.customerId),
      places.site(line.siteId),
      line.orderTitle,
    ]
      .map(csvCell)
      .join(','),
  );
  return [header.map(csvCell).join(','), ...rows].join('\n');
}
